/**
 * Shop Creator drawing helpers: glow brighten + RGBA paint.
 */

export function clampByte(n: number) {
  return Math.max(0, Math.min(255, Math.round(n)));
}

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.replace(/./g, (c) => c + c) : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Push color toward white for a bright “glow” look. */
export function brightenHex(hex: string, amount = 0.55): string {
  const [r, g, b] = hexToRgb(hex);
  const t = Math.max(0, Math.min(1, amount));
  const br = clampByte(r + (255 - r) * t);
  const bg = clampByte(g + (255 - g) * t);
  const bb = clampByte(b + (255 - b) * t);
  return `#${[br, bg, bb].map((n) => n.toString(16).padStart(2, "0")).join("")}`;
}

export function rgbaCss(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  const a = Math.max(0, Math.min(1, alpha));
  return `rgba(${r},${g},${b},${a})`;
}

/** Paint one pixel, optionally with a soft bright halo (glow). */
export function paintPixel(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  color: string | null,
  opts: { glow?: boolean; canvasW: number; canvasH: number },
) {
  if (!color) {
    ctx.clearRect(x, y, 1, 1);
    return;
  }
  if (!opts.glow) {
    ctx.fillStyle = color;
    ctx.fillRect(x, y, 1, 1);
    return;
  }
  const bright = brightenHex(color, 0.6);
  const w = opts.canvasW;
  const h = opts.canvasH;
  const halo: Array<[number, number, number]> = [
    [0, 0, 1],
    [1, 0, 0.4],
    [-1, 0, 0.4],
    [0, 1, 0.4],
    [0, -1, 0.4],
    [1, 1, 0.22],
    [-1, 1, 0.22],
    [1, -1, 0.22],
    [-1, -1, 0.22],
  ];
  for (const [dx, dy, a] of halo) {
    const px = x + dx;
    const py = y + dy;
    if (px < 0 || py < 0 || px >= w || py >= h) continue;
    ctx.fillStyle = rgbaCss(bright, a);
    ctx.fillRect(px, py, 1, 1);
  }
}

export const MAX_ANIM_FRAMES = 10;
export const ANIM_FRAME_MS = 110;
